import { cva, type VariantProps } from 'class-variance-authority';
import type { FC } from 'react';
import { cn } from '../lib/utils';
import { DATA_TYPE_INFO, type DataType } from './data-types';

export const dataTypeIconVariants = cva(
  'inline-flex shrink-0 items-center justify-center',
  {
    variants: {
      variant: {
        /** Bare glyph beside a type name. */
        glyph: 'text-muted-foreground',
        /** Boxed, for pickers and schema lists where the icon stands alone. */
        tile: 'rounded-md border border-border bg-surface-hover text-[color:var(--z-text-secondary)]'
      },
      size: {
        sm: '[&_svg]:size-2.5',
        md: '[&_svg]:size-3.5'
      }
    },
    compoundVariants: [
      { variant: 'tile', size: 'sm', className: 'size-5' },
      { variant: 'tile', size: 'md', className: 'size-7' }
    ],
    defaultVariants: { variant: 'glyph', size: 'sm' }
  }
);

interface DataTypeIconProps extends VariantProps<typeof dataTypeIconVariants> {
  type: DataType;
  /**
   * Name the icon for assistive tech ("Integer") when no visible type label
   * sits beside it. Omit it to keep the icon decorative.
   */
  label?: boolean;
  className?: string;
}

/** The icon for a field's type: `#` for int, `T` for string, a calendar for dates. */
export const DataTypeIcon: FC<DataTypeIconProps> = ({
  type,
  variant,
  size,
  label = false,
  className
}) => {
  const { icon: Icon, label: name } = DATA_TYPE_INFO[type];
  return (
    <span
      {...(label ? { role: 'img', 'aria-label': name } : { 'aria-hidden': true })}
      className={cn(dataTypeIconVariants({ variant, size }), className)}
    >
      <Icon />
    </span>
  );
};
